import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Panel, StatusBadge, EmptyState } from "@/components/admin/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LEAD_STATUSES, SCHEDULE_STAGES, waLink, waMessage } from "@/lib/constants";
import { StageScheduleDialog } from "@/components/admin/StageScheduleDialog";
import { ArrowLeft, Phone, MessageCircle, MapPin, User, Calendar, ShieldCheck, FileImage } from "lucide-react";
import { toast } from "sonner";
import { ProfileNotes, ProfileFollowups, ProfileLeads } from "@/components/admin/ProfileWorkspace";
import { ConversionDialog } from "@/components/admin/ConversionDialog";

export default function VisitorProfile() {
  const { visitorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [saleLead, setSaleLead] = useState(null);
  const [pendingStage, setPendingStage] = useState(null);

  const load = async () => setData((await api.get(`/visitors/${visitorId}`)).data);
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)); }, [visitorId]);

  const applyStage = async (target, s, extra = {}) => {
    try {
      const res = target ? await api.patch(`/leads/${target.lead_id}`, { status: s, ...extra }) : await api.patch(`/visitors/${visitorId}/stage`, { status: s, ...extra });
      toast.success("Stage updated"); load();
      const leadId = target?.lead_id || res.data.lead_id;
      if (s === "Sale done") setSaleLead({ visitor_id: visitorId, lead_id: leadId, sales_executive: target?.assigned_to, name: data.visitor.full_name });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update stage"); }
  };
  const requestStage = (target, s, current) => {
    if (SCHEDULE_STAGES.includes(s) && s !== current) { setPendingStage({ target, stage: s, name: data.visitor.full_name }); return; }
    applyStage(target, s);
  };
  const changeStatus = (s) => requestStage(null, s, currentStage);
  const changeStage = (l, s) => requestStage(l, s, l.status);

  if (!data) return <div className="py-20 text-center text-slate-400">Loading…</div>;
  const { visitor: v, visits, leads, conversions, followups } = data;
  const currentStage = v.lead_stage || leads[0]?.status || "New";

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-slate-500 mb-4 hover:text-[#081226]"><ArrowLeft size={16} /> Back</button>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-5">
          <Panel>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center font-display font-extrabold text-xl text-white" style={{ background: "linear-gradient(135deg,#0f2042,#081226)" }}>
                {v.full_name?.[0]}
              </div>
              <h2 className="font-display text-xl font-extrabold text-[#081226] mt-3" data-testid="profile-name">{v.full_name}</h2>
              <p className="font-mono-num text-xs text-[#B8860B] font-bold">{v.visitor_id}</p>
              {v.total_visits > 1 && <p className="mt-2 text-[11px] font-bold px-3 py-1 rounded-full bg-[#E8752A]/15 text-[#B8860B] inline-block">RETURNING · {v.total_visits} visits</p>}
              <div className="mt-4" data-testid="profile-stage-badge"><StatusBadge status={currentStage} /></div>
            </div>
            <div className="mt-5 space-y-2.5 text-sm">
              <Row icon={Phone} label={v.mobile} />
              <Row icon={User} label={`${v.gender || "—"}${v.age ? `, ${v.age} yrs` : ""}`} />
              <Row icon={MapPin} label={v.area || "—"} />
              <Row icon={Calendar} label={`First visit ${v.first_visit?.slice(0, 10)}`} />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <a href={waLink(v.mobile, waMessage(v.full_name, v.interests?.[0]))} target="_blank" rel="noreferrer" data-testid="profile-whatsapp-btn" className="h-11 rounded-xl bg-[#25D366] text-white font-bold flex items-center justify-center gap-2 text-sm"><MessageCircle size={16} /> WhatsApp</a>
              <a href={`tel:${v.mobile}`} data-testid="profile-call-btn" className="h-11 rounded-xl bg-[#081226] text-white font-bold flex items-center justify-center gap-2 text-sm"><Phone size={16} /> Call</a>
            </div>
            <div className="mt-4">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Update Status</label>
              <Select value={currentStage} onValueChange={changeStatus}>
                <SelectTrigger className="mt-1.5 h-10 rounded-xl" data-testid="profile-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Panel>

          <Panel title="Interests">
            <div className="flex flex-wrap gap-2">
              {v.interests?.length ? v.interests.map((i) => <span key={i} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-slate-600">{i}</span>) : <EmptyState text="No interests recorded" />}
            </div>
          </Panel>

          {(v.ekyc_verified || v.aadhaar_front_id || v.aadhaar_back_id) && (
            <Panel title="Identity & Aadhaar">
              {v.ekyc_verified && (
                <div className="flex items-center gap-2 mb-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                  <ShieldCheck size={16} className="text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-700">Aadhaar e-KYC verified (Demo)</span>
                </div>
              )}
              {(v.aadhaar_front_id || v.aadhaar_back_id) ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {["aadhaar_front_id", "aadhaar_back_id"].map((k) => v[k] && (
                      <a key={k} href={`${API}/ekyc/doc/${v[k]}`} target="_blank" rel="noreferrer" data-testid={`profile-${k}`}
                        className="relative block rounded-lg overflow-hidden border border-slate-200 group">
                        <img src={`${API}/ekyc/doc/${v[k]}`} alt={k} className="w-full aspect-[1.58/1] object-cover group-hover:opacity-90" />
                        <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded capitalize">{k.includes("front") ? "Front" : "Back"}</span>
                      </a>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><FileImage size={12} /> Visitor-uploaded · handle as confidential</p>
                </>
              ) : (
                <p className="text-xs text-slate-400">No card images uploaded.</p>
              )}
            </Panel>
          )}
        </div>

        <div className="lg:col-span-2 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <ProfileNotes visitorId={visitorId} visitorNotes={v.notes} leads={leads} onChange={load} />
            <ProfileFollowups visitorId={visitorId} followups={followups} team={team} onChange={load} />
          </div>

          <Panel title={`Visit History (${visits.length})`}>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {visits.map((vi) => (
                <div key={vi.visit_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 text-sm">
                  <div><p className="font-semibold text-[#081226]">{vi.visiting_for}</p><p className="text-xs text-slate-400">{vi.qr_source} · {vi.source}</p></div>
                  <div className="text-right text-xs text-slate-500 font-mono-num">{vi.entry_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Status Timeline">
            <div className="space-y-3">
              {v.status_history?.slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#E8752A]" />
                  <StatusBadge status={h.lead_stage || h.status} />
                  <span className="text-xs text-slate-400 font-mono-num ml-auto">{h.at?.slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          </Panel>

          <div className="grid sm:grid-cols-2 gap-5">
            <ProfileLeads leads={leads} onStage={changeStage} />
            <Panel title={`Conversions (${conversions.length})`}>
              {conversions.length ? conversions.map((c) => (
                <div key={c.conversion_id} className="p-3 rounded-xl bg-emerald-50 mb-2 text-sm">
                  <p className="font-semibold text-[#081226]">{c.membership_type || "Sale details pending"}{c.tenure ? ` · ${c.tenure}` : ""}</p>
                  <p className="text-emerald-700 font-mono-num font-bold">₹{(c.amount || 0).toLocaleString()}</p>
                  {c.details_pending && <button onClick={() => setSaleLead({ ...c, name: v.full_name })} className="text-xs font-bold text-[#D95D1E] mt-1" data-testid="profile-add-sale-details-btn">Add sale details</button>}
                </div>
              )) : <EmptyState text="Not converted yet" />}
            </Panel>
          </div>
        </div>
      </div>
      <StageScheduleDialog pending={pendingStage} onCancel={() => setPendingStage(null)}
        onConfirm={async (scheduled_at, stage_notes) => { await applyStage(pendingStage.target, pendingStage.stage, { scheduled_at, stage_notes }); setPendingStage(null); }} />
      <ConversionDialog open={!!saleLead} onClose={() => setSaleLead(null)} initial={saleLead} team={team} onSaved={load} title={`Sale Details — ${saleLead?.name || ""}`} />
    </div>
  );
}

const Row = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2.5 text-slate-600"><Icon size={15} className="text-slate-400" /> <span className="font-medium">{label}</span></div>
);
